import { createTheme } from '@mui/material/styles';
import { colors, radii, spacingUnit, typography as type } from './tokens';

/**
 * The application's Material UI theme.
 *
 * The palette is the "Ocean blue with a brass accent" used by the React Native
 * client — the two are one product, and someone moving between phone and
 * desktop should not feel they have changed applications.
 *
 * Everything visual is decided here. Components elsewhere use MUI props and
 * `sx` with theme keys; none of them names a colour, a radius or a font size,
 * which is what makes a rebrand a change to this file alone.
 *
 * Light and dark are declared as colour schemes rather than as two themes.
 * With `cssVariables` on, MUI emits both palettes as CSS custom properties and
 * switches between them on the user's system preference, so no component has to
 * know which mode it is in.
 */

/** A base theme, used only to build augmented palette colours from. */
const base = createTheme();

/** Shared between the two schemes: the brand does not change with the lights. */
const accent = base.palette.augmentColor({
  color: { main: colors.accent },
  name: 'accent'
});

const lightPalette = {
  primary: {
    main: colors.primary,
    dark: colors.primaryDark,
    light: colors.gradientStart,
    contrastText: colors.textInverse
  },
  secondary: { main: colors.secondary, contrastText: colors.textInverse },
  accent,
  error: { main: colors.danger },
  success: { main: colors.success },
  background: { default: colors.background, paper: colors.surfaceRaised },
  text: { primary: colors.text, secondary: colors.textMuted },
  divider: colors.border
};

/** The ocean hue lifted for a dark ground, so the brand still reads as itself. */
const darkPalette = {
  primary: {
    main: '#3EA6D0',
    dark: '#0E86B8',
    light: '#6FC3E4',
    contrastText: '#04222F'
  },
  secondary: { main: '#6FB3C6', contrastText: '#04222F' },
  accent: base.palette.augmentColor({ color: { main: '#D5A75C' }, name: 'accent' }),
  error: { main: '#F08876' },
  success: { main: '#59C98A' },
  background: { default: '#061E29', paper: '#0B2E3D' },
  text: { primary: '#E7F2F6', secondary: '#9DBECB' },
  divider: '#1D5064'
};

export const theme = createTheme({
  cssVariables: true,
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette }
  },

  spacing: spacingUnit,
  shape: { borderRadius: radii.md },

  typography: {
    fontFamily: type.fontFamily,
    fontSize: type.basePx,
    h1: { fontSize: type.h1, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h2: { fontSize: type.h2, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: type.h3, fontWeight: 600, lineHeight: 1.35 },
    body1: { fontSize: type.body, lineHeight: 1.5 },
    body2: { fontSize: type.caption, lineHeight: 1.5 },
    caption: { fontSize: type.caption },
    button: { fontWeight: 600, textTransform: 'none' }
  },

  components: {
    /**
     * Buttons keep their sentence case (textTransform above) — SHOUTING LABELS
     * are harder to read and do not match the mobile client.
     */
    MuiButton: {
      defaultProps: { variant: 'contained', disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radii.md, minHeight: 40 }
      }
    },

    /** Outlined inputs everywhere, so no form has to choose a variant. */
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium', fullWidth: true }
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' }
      }
    },

    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: radii.lg }
      }
    },

    MuiAppBar: {
      defaultProps: { color: 'inherit', elevation: 0 },
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: t.vars ? t.vars.palette.background.paper : t.palette.background.paper,
          borderBottom: `1px solid ${t.vars ? t.vars.palette.divider : t.palette.divider}`
        })
      }
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 }
      }
    },

    MuiAlert: {
      defaultProps: { variant: 'outlined' }
    },

    /**
     * A visible focus ring on every focusable element, not just the ones MUI
     * styles by default. Keyboard users lose the thread without it.
     */
    MuiCssBaseline: {
      styleOverrides: {
        ':focus-visible': {
          outline: '2px solid currentColor',
          outlineOffset: 2
        },
        'html, body': {
          height: '100%'
        },
        /**
         * min-height, not height.
         *
         * A fixed 100% pins the mount point to exactly one viewport, so a page
         * taller than the screen overflows it instead of extending it — the
         * sticky app bar then stops sticking partway down and a blank band
         * appears above the content. min-height lets the root grow with the
         * page while still filling a short one, which is what keeps the footer
         * at the bottom.
         */
        '#root': {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%'
        }
      }
    }
  }
});
